import React from 'react';
import { useBoolean, useIsMounted } from 'utils';
import { getQrCodeDetector } from './utils';

export type QrCodeScannerArgs = {
  cameraFacingMode?: 'environment' | 'user';
  isEnabled: boolean;
  onDetected: (rawValue: string) => Promise<boolean> | boolean;
  onError?: (error: Error) => void;
};

export type QrCodeScannerResult = {
  videoRef: React.MutableRefObject<HTMLVideoElement | null>;
  error: Error | null;
  isInitializing: boolean;
};

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  return 'Unable to scan QR code. Enter the value manually.';
};

export function useQrCodeScanner(args: QrCodeScannerArgs): QrCodeScannerResult {
  const {
    cameraFacingMode = 'environment',
    isEnabled,
    onDetected,
    onError,
  } = args;

  const isMounted = useIsMounted();

  const onDetectedRef = React.useRef(onDetected);
  onDetectedRef.current = onDetected;
  const onErrorRef = React.useRef(onError);
  onErrorRef.current = onError;

  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const streamRef = React.useRef<MediaStream | null>(null);
  const frameRef = React.useRef<number | null>(null);
  const isProcessingRef = React.useRef(false);

  const {
    value: isInitializing,
    setTrue: startInitialization,
    setFalse: endInitialization,
  } = useBoolean(false);
  const [scannerError, setScannerError] = React.useState<Error | null>(null);

  const stopScanner = React.useCallback((): void => {
    if (frameRef.current) {
      window.cancelAnimationFrame(frameRef.current);
      frameRef.current = null;
    }

    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    isProcessingRef.current = false;
  }, []);

  React.useEffect(() => {
    if (!isEnabled) {
      stopScanner();
      return undefined;
    }

    const handleError = (error: unknown): void => {
      const nextError = new Error(getErrorMessage(error));
      setScannerError(nextError);
      onErrorRef.current?.(nextError);
    };

    /**
     * Cancellation token for this effect invocation. Set to true by the cleanup
     * function when the effect re-runs or the component unmounts, signalling any
     * in-flight async operations (getUserMedia, detect, onDetected) to bail out
     * without side effects.
     */
    let isCancelled = false;

    const startScanner = async (): Promise<void> => {
      startInitialization();
      setScannerError(null);

      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error(
            'Camera access is not supported in this browser. Enter the value manually.',
          );
        }

        const detector = await getQrCodeDetector();

        const stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: cameraFacingMode },
          },
        });

        if (isCancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }

        streamRef.current = stream;

        if (!videoRef.current) return;

        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute('playsInline', 'true');
        await videoRef.current.play();

        const scanFrame = async (): Promise<void> => {
          if (isCancelled || !videoRef.current || isProcessingRef.current) {
            return;
          }

          if (
            videoRef.current.readyState >= HTMLMediaElement.HAVE_ENOUGH_DATA
          ) {
            try {
              const detectedCodes = await detector.detect(videoRef.current);
              const rawValue = detectedCodes.find(
                (code) => code.rawValue,
              )?.rawValue;

              if (rawValue) {
                isProcessingRef.current = true;
                const isAccepted = await onDetectedRef.current(rawValue);
                if (isCancelled) return;
                isProcessingRef.current = false;

                if (isAccepted) {
                  stopScanner();
                  return;
                }
              }
            } catch (error) {
              handleError(error);
              return;
            }
          }

          // recursively scan the next frame
          frameRef.current = window.requestAnimationFrame(() => {
            void scanFrame();
          });
        };

        // start scanning frames
        frameRef.current = window.requestAnimationFrame(() => {
          void scanFrame();
        });
      } catch (error) {
        handleError(error);
      } finally {
        if (isMounted()) {
          endInitialization();
        }
      }
    };

    void startScanner();

    return (): void => {
      isCancelled = true;
      stopScanner();
    };
  }, [cameraFacingMode, isEnabled, stopScanner]);

  return {
    videoRef,
    isInitializing,
    error: scannerError,
  };
}
